import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Switch, Radio, RadioGroup, FormControlLabel, Paper, Button } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';

interface StepNotificationSettingsProps {
  onCancel?: () => void;
  onNext?: () => void;
  onBack?: () => void;
  value?: {
    email: boolean;
    push: boolean;
    consolidate: string;
    overdue: boolean;
    manual: boolean;
  };
  onChange?: (v: { email: boolean; push: boolean; consolidate: string; overdue: boolean; manual: boolean }) => void;
}

const StepNotificationSettings: React.FC<StepNotificationSettingsProps> = ({
  onCancel,
  onNext,
  onBack,
  value,
  onChange,
}) => {
  const [email, setEmail] = useState(value?.email ?? true);
  const [push, setPush] = useState(value?.push ?? false);
  const [consolidate, setConsolidate] = useState(value?.consolidate ?? 'daily');
  const [overdue, setOverdue] = useState(value?.overdue ?? true);
  const [manual, setManual] = useState(value?.manual ?? false);

  useEffect(() => {
    if (onChange) {
      onChange({ email, push, consolidate, overdue, manual });
    }
  }, [email, push, consolidate, overdue, manual]);

  useEffect(() => {
    if (value) {
      setEmail(value.email);
      setPush(value.push);
      setConsolidate(value.consolidate);
      setOverdue(value.overdue);
      setManual(value.manual);
    }
  }, [value]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '80vh', width: '100%' }}>
      <Paper
        sx={{
          p: 4,
          maxWidth: 900,
          width: '100%',
          mt: 8,
          minHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack spacing={4}>
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t('dtbs.assignments.stepper.notificationsSettings')}
            </Typography>
            <Stack
              direction='row'
              spacing={4}
              alignItems='center'
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={email}
                    onChange={() => setEmail((v) => !v)}
                    color='success'
                  />
                }
                label={
                  <>
                    <Typography sx={{ fontWeight: 600 }}>{t('dtbs.assignments.stepper.emailNotifications')}</Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('dtbs.assignments.stepper.sendEmailNotifications')}
                    </Typography>
                  </>
                }
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={push}
                    onChange={() => setPush((v) => !v)}
                    color='success'
                  />
                }
                label={
                  <>
                    <Typography sx={{ fontWeight: 600 }}>{t('dtbs.assignments.stepper.pushNotifications')}</Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('dtbs.assignments.stepper.sendPushNotifications')}
                    </Typography>
                  </>
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
          <Box>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {t('dtbs.assignments.stepper.consolidateNotifications')}
            </Typography>
            <RadioGroup
              row
              value={consolidate}
              onChange={(e) => setConsolidate(e.target.value)}
            >
              <FormControlLabel
                value='daily'
                control={<Radio color='success' />}
                label={t('dtbs.assignments.stepper.daily')}
              />
              <FormControlLabel
                value='weekly'
                control={<Radio color='success' />}
                label={t('dtbs.assignments.stepper.weekly')}
              />
            </RadioGroup>
          </Box>
          <Box>
            <Stack
              direction='row'
              spacing={4}
              alignItems='center'
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={overdue}
                    onChange={() => setOverdue((v) => !v)}
                    color='success'
                  />
                }
                label={
                  <>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t('dtbs.assignments.stepper.overdueNotifications')}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('dtbs.assignments.stepper.sendOverdueNotifications')}
                    </Typography>
                  </>
                }
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={manual}
                    onChange={() => setManual((v) => !v)}
                    color='success'
                  />
                }
                label={
                  <>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t('dtbs.assignments.stepper.manualReminderNotifications')}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('dtbs.assignments.stepper.enableManualReminders')}
                    </Typography>
                  </>
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Typography variant='body2'>
            {t('dtbs.assignments.stepper.step').replace('{{current}}', '2').replace('{{total}}', '3')}
          </Typography>
          <Stack
            direction='row'
            spacing={2}
          >
            <Button
              variant='outlined'
              onClick={onBack}
            >
              {t('dtbs.assignments.stepper.back')}
            </Button>
            <Button
              variant='contained'
              onClick={onNext}
            >
              {t('dtbs.assignments.stepper.continue')}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default StepNotificationSettings;
